import type { FullUser, UserProfile } from '@/graphql/__generated/resolvers-types';

/** Flat Adobe keys written to utag_data / page-load snapshots. */
export const SUBSCRIBER_UTAG_FIELD_KEYS = [
    'userRegId',
    'userType',
    'userStatus',
    'userProfileId',
    'userProfileRefId',
    'userProfileCategory',
    'userProfileMaster',
    'packageSource',
    'packageStatus',
] as const;

/**
 * Flat Adobe / Tealium fields for page loads (trackState / utag_data) and action events.
 * Aligns with sign-in flows: userType = subscription tier description, userStatus = sb|* tracking.
 */
export function buildAdobeUserTrackingFields(
    fullUser: FullUser | undefined | null,
    profile?: UserProfile | null | undefined,
): Record<string, string> {
    const pkg = fullUser?.cbsPackageInfo?.[0];
    const p = profile ?? fullUser?.activeProfile;
    const userRegId =
        p?.userId ||
        fullUser?.accountProfiles?.find((accountProfile) => accountProfile?.userId)?.userId ||
        fullUser?.providerUserId ||
        fullUser?.paramountStreamingId ||
        '';

    return {
        userRegId,
        userType: fullUser?.userStatus?.description ?? '',
        userStatus: fullUser?.packageStatusTracking ?? '',
        userProfileId: p?.id ?? '',
        userProfileRefId: p?.referenceProfileId ?? '',
        userProfileCategory: (p?.profileType as string | undefined) ?? '',
        userProfileMaster: p?.isMasterProfile != null ? String(p.isMasterProfile) : '',
        packageSource: pkg?.packageSource ?? '',
        packageStatus: pkg?.packageStatus ?? '',
    };
}

export function clearSubscriberUtagFields(): void {
    if (typeof window === 'undefined' || !window.utag_data) {
        return;
    }

    for (const key of SUBSCRIBER_UTAG_FIELD_KEYS) {
        delete window.utag_data[key];
    }
}

function hasNonEmptySubscriberFieldValue(value: unknown): boolean {
    return typeof value === 'string' ? value.length > 0 : value != null;
}

/** Whether merged Adobe getState() still carries subscriber page-load fields. */
export function hasSubscriberAdobeDataLayerState(
    state: Record<string, unknown> | null | undefined,
): boolean {
    if (!state) {
        return false;
    }

    for (const key of SUBSCRIBER_UTAG_FIELD_KEYS) {
        if (hasNonEmptySubscriberFieldValue(state[key])) {
            return true;
        }
    }

    const userInfo = state.userInfo as Record<string, unknown> | null | undefined;
    if (!userInfo || typeof userInfo !== 'object') {
        return false;
    }

    if (userInfo.isAuthenticated === true) {
        return true;
    }

    for (const key of SUBSCRIBER_UTAG_FIELD_KEYS) {
        if (hasNonEmptySubscriberFieldValue(userInfo[key])) {
            return true;
        }
    }

    return false;
}

/** Empty flat subscriber keys for Adobe pushes that must overwrite stale getState(). */
export function buildClearedSubscriberFlatAdobeFields(): Record<string, string> {
    return Object.fromEntries(SUBSCRIBER_UTAG_FIELD_KEYS.map((key) => [key, ''])) as Record<
        string,
        string
    >;
}

/** Clears subscriber flat keys and userInfo from merged Adobe getState(). */
export function buildClearedSubscriberAdobeDataLayerFields(): Record<string, unknown> {
    return {
        ...buildClearedSubscriberFlatAdobeFields(),
        userInfo: null,
    };
}
